import React from 'react';

import { Text } from '@reckon-web/text';
import { Flex } from '@reckon-web/flex';
import { makeId } from '@reckon-web/utils';

import { TaskStatusEnumValue } from '../../work/types/task-status-enum';
import { taskStatuses } from '../../../globals';
import { Task } from '../../../hooks/use-query-tasks';
import { useUpdateTaskStatus } from '../../../hooks/use-update-task-status';
import {
  IDraggable,
  IDroppable,
  Kanban,
  KanbanDraggable,
} from '../../common/kanban';
import { TaskStatusBadge } from '../../common/task-status-badge';
import { DraggableCard } from '../../work/tasks/kanban-view';
import { CreateTaskOptions } from '../../task/create-drawer';
import { JobStatusEnum } from '../types/job-status-enum';

const statusTitleHeight = '42px';

interface DraggableType extends IDraggable {
  task: Task;
  locked?: boolean;
  onClick?: () => void;
}

interface DroppableType extends IDroppable<DraggableType> {
  status: TaskStatusEnumValue;
  testId?: string;
}

const reducer = (draggable: DraggableType, droppable: DroppableType) => ({
  ...draggable,
  task: {
    ...draggable.task,
    status: droppable.status,
  },
});

const Title = ({
  status,
  draggables,
  testId = `kanban-task-card-title`,
}: DroppableType) => (
  <Flex
    alignItems="center"
    gap="xsmall"
    padding="small"
    height={statusTitleHeight}
    data={{ testid: testId }}
  >
    <TaskStatusBadge status={status} testId={makeId(testId, 'badge')} />
    <Text
      size="xsmall"
      weight="medium"
      color="dim"
      data={{ testid: makeId(testId, 'draggables') }}
    >
      ({draggables.length})
    </Text>
  </Flex>
);
// Recommended performance optimisation - memoize to prevent unnecessary re-renders
const MemoisedTitle = React.memo(Title);

const Body: React.FC<DroppableType & { Draggable: KanbanDraggable }> = ({
  id,
  draggables,
  Draggable,
}) => (
  <Flex direction="column" gap="small">
    {draggables.map((draggable, i) => (
      <Draggable
        key={draggable.id}
        draggableId={draggable.id}
        droppableId={id}
        index={i}
        isDragDisabled={draggable.locked}
        onClick={draggable.onClick}
      >
        <DraggableCard {...draggable} />
      </Draggable>
    ))}
  </Flex>
);
// Recommended performance optimisation - memoize to prevent unnecessary re-renders
const MemoisedBody = React.memo(Body);

export const KanbanView: React.FC<{
  tasks: Task[];
  compare: (a: Task, b: Task) => number;
  onClickTask?: (taskId: string) => void;
  onClickCreateTask?: (options?: CreateTaskOptions) => void;
  readonly?: boolean;
  testId?: string;
}> = ({
  tasks,
  compare,
  onClickTask,
  onClickCreateTask,
  readonly,
  testId = `tasks__kanban-view`,
}) => {
  const [UpdateTaskStatusDialog, updateTaskStatus] = useUpdateTaskStatus();

  const draggables = React.useMemo<DraggableType[]>(
    () =>
      tasks.map((task) => ({
        id: task.cloudId || '',
        task,
        locked:
          task.job?.status.status === JobStatusEnum.valuesEnum.Closed ||
          readonly,
        onClick: onClickTask && (() => onClickTask(task.cloudId || '')),
      })),
    [tasks, onClickTask, readonly]
  );

  const sort = React.useCallback<
    (draggables: DraggableType[]) => DroppableType[]
  >(
    (draggables: DraggableType[]): DroppableType[] =>
      taskStatuses.map(({ id: status }) => ({
        id: status,
        status,
        draggables: draggables
          .filter(({ task }) => task.status === status)
          .sort(({ task: a }, { task: b }) => compare(a, b)),
      })),
    [compare]
  );

  const resolver = React.useCallback(
    (draggable: DraggableType, droppable: DroppableType) => {
      const { task } = draggable;
      const { status } = droppable;
      return updateTaskStatus({ task, status });
    },
    [updateTaskStatus]
  );

  return (
    <>
      <Kanban<DraggableType, DroppableType>
        draggables={draggables}
        sort={sort}
        reducer={reducer}
        resolver={resolver}
        Title={MemoisedTitle}
        Body={MemoisedBody}
        columnBgColour="dim"
        testId={makeId(testId, 'kanban')}
      />
      <UpdateTaskStatusDialog />
    </>
  );
};
